<!-- FAQ Management Page -->
<div class="p-4 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
  
  <!-- Page Header -->
  <div class="mb-8">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl md:text-4xl font-bold text-slate-800 mb-2">FAQ Management</h1>
        <p class="text-slate-600">Manage your knowledge base and frequently asked questions</p>
      </div>
      
      <!-- Toggle Form Button -->
      <button 
        (click)="toggleForm()"
        class="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2">
        <svg class="w-5 h-5 transition-transform duration-300" [class.rotate-45]="formVisible" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        <span>{{ formVisible ? 'Hide Form' : 'Add New FAQ' }}</span>
      </button>
    </div>
  </div>

  <!-- Add/Edit FAQ Form -->
  <div #faqFormContainer *ngIf="formVisible" class="faq-form-container bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-slate-200 hover:shadow-2xl transition-all duration-300" style="animation: slideDown 0.3s ease-out;">
    <div class="flex items-center gap-3 mb-6">
      <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
      </div>
      <div>
        <h2 class="text-xl font-bold text-slate-800">{{ editingId ? 'Update FAQ' : 'Add New FAQ' }}</h2>
        <p class="text-sm text-slate-500">Fill in the details below</p>
      </div>
    </div>

    <form (ngSubmit)="addOrUpdate()">
      <div class="grid gap-4 grid-cols-1 md:grid-cols-2 mb-4">
        <!-- Category Input -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            <span class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
              </svg>
              Category
            </span>
          </label>
          <input 
            [(ngModel)]="newFaq.category" 
            name="category" 
            placeholder="e.g., Network, Security, POS" 
            class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
            required />
        </div>

        <!-- Keywords Input -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            <span class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
              </svg>
              Keywords
            </span>
          </label>
          <input 
            [(ngModel)]="newFaq.keywords" 
            name="keywords" 
            placeholder="wifi, router, connection (comma separated)" 
            class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none" />
        </div>
      </div>

      <!-- Question Input -->
      <div class="mb-4">
        <label class="block text-sm font-semibold text-slate-700 mb-2">
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Question
          </span>
        </label>
        <input 
          [(ngModel)]="newFaq.question" 
          name="question" 
          placeholder="What question will users ask?" 
          class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
          required />
      </div>

      <!-- Answer Textarea -->
      <div class="mb-6">
        <label class="block text-sm font-semibold text-slate-700 mb-2">
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Answer
          </span>
        </label>
        <textarea 
          [(ngModel)]="newFaq.answer" 
          name="answer" 
          placeholder="Provide a detailed answer..." 
          rows="4"
          class="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none resize-none"
          required></textarea>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center justify-end gap-3">
        <button 
          type="button" 
          (click)="reset()"
          class="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all duration-200 hover:scale-105">
          Reset
        </button>
        <button 
          type="submit"
          class="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          {{ editingId ? 'Update FAQ' : 'Add FAQ' }}
        </button>
      </div>
    </form>
  </div>

  <!-- FAQs List Section -->
  <div class="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
    <!-- Section Header -->
    <div class="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-bold text-white">All FAQs</h2>
            <p class="text-sm text-emerald-100">{{ faqs?.length || 0 }} total questions</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Table View (>650px) -->
    <div class="hidden min-[651px]:block overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-slate-50 border-b-2 border-slate-200">
            <th class="px-6 py-4 text-left">
              <span class="text-sm font-bold text-slate-700 uppercase tracking-wider">Category</span>
            </th>
            <th class="px-6 py-4 text-left">
              <span class="text-sm font-bold text-slate-700 uppercase tracking-wider">Question</span>
            </th>
            <th class="px-6 py-4 text-left">
              <span class="text-sm font-bold text-slate-700 uppercase tracking-wider">Answer</span>
            </th>
            <th class="px-6 py-4 text-center">
              <span class="text-sm font-bold text-slate-700 uppercase tracking-wider">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-200">
          <tr *ngFor="let f of faqs; let i = index" 
              class="hover:bg-emerald-50 transition-colors duration-150 group">
            <!-- Category -->
            <td class="px-6 py-4">
              <span class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 rounded-lg text-sm font-semibold">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                {{ f.category }}
              </span>
            </td>
            
            <!-- Question -->
            <td class="px-6 py-4">
              <p class="text-slate-800 font-medium line-clamp-2">{{ f.question }}</p>
            </td>
            
            <!-- Answer -->
            <td class="px-6 py-4">
              <p class="text-slate-600 text-sm line-clamp-2">{{ f.answer }}</p>
            </td>
            
            <!-- Actions -->
            <td class="px-6 py-4">
              <div class="action-buttons">
                <button 
                  (click)="edit(f)"
                  class="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-all duration-200 hover:scale-110 group/btn">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </button>
                <button 
                  (click)="delete(f.id)"
                  class="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all duration-200 hover:scale-110 group/btn">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Empty State for Desktop -->
      <div *ngIf="!faqs || faqs.length === 0" class="py-16 text-center">
        <div class="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">No FAQs Yet</h3>
        <p class="text-slate-500">Start by adding your first FAQ above</p>
      </div>
    </div>

    <!-- Mobile Card View (≤650px) -->
    <div class="min-[651px]:hidden p-4 space-y-4">
      <div *ngFor="let f of faqs; let i = index" 
           class="faq-card bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-lg border-2 border-slate-200 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl overflow-hidden">
        
        <!-- Card Header -->
        <div class="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-3">
          <div class="flex items-center justify-between">
            <span class="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-lg text-xs font-semibold">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
              </svg>
              {{ f.category }}
            </span>
            <span class="text-xs text-white/80 font-medium">#{{ i + 1 }}</span>
          </div>
        </div>

        <!-- Card Body -->
        <div class="p-4 space-y-3">
          <!-- Question -->
          <div>
            <div class="flex items-start gap-2 mb-2">
              <svg class="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div class="flex-1">
                <p class="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Question</p>
                <p class="text-sm font-bold text-slate-800 leading-snug">{{ f.question }}</p>
              </div>
            </div>
          </div>

          <!-- Answer -->
          <div>
            <div class="flex items-start gap-2">
              <svg class="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <div class="flex-1">
                <p class="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1">Answer</p>
                <p class="text-sm text-slate-600 leading-relaxed">{{ f.answer }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Card Footer Actions -->
        <div class="bg-slate-50 px-4 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
          <button 
            (click)="edit(f)"
            class="flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-md">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            Edit
          </button>
          <button 
            (click)="delete(f.id)"
            class="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-md">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            Delete
          </button>
        </div>
      </div>

      <!-- Empty State for Mobile -->
      <div *ngIf="!faqs || faqs.length === 0" class="py-12 text-center">
        <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg class="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-slate-700 mb-1">No FAQs Yet</h3>
        <p class="text-sm text-slate-500">Add your first FAQ above</p>
      </div>
    </div>
  </div>
</div>
